import { TZDate } from '@date-fns/tz';
import { format, startOfMonth, endOfMonth } from 'date-fns';

/**
 * 店舗タイムゾーンでの現在時刻を取得
 */
export function nowInShopTimezone(timezone: string = 'Asia/Tokyo'): TZDate {
  return new TZDate(new Date(), timezone);
}

/**
 * タイムスタンプから営業日文字列（YYYY-MM-DD）を算出
 * boundaryHour=6 の場合、午前2時のタイムスタンプは前日の営業日として返す
 */
export function getBusinessDate(
  timestamp: Date,
  timezone: string = 'Asia/Tokyo',
  boundaryHour: number = 0
): string {
  const tzDate = new TZDate(timestamp, timezone);
  const hour = tzDate.getHours();

  if (boundaryHour > 0 && hour < boundaryHour) {
    // 区切り時刻より前 → 前日の営業日
    const prevDay = new TZDate(
      tzDate.getFullYear(),
      tzDate.getMonth(),
      tzDate.getDate() - 1,
      timezone
    );
    return format(prevDay, 'yyyy-MM-dd');
  }

  return format(tzDate, 'yyyy-MM-dd');
}

/**
 * 今日の営業日文字列を取得
 */
export function todayBusinessDate(
  timezone: string = 'Asia/Tokyo',
  boundaryHour: number = 0
): string {
  return getBusinessDate(new Date(), timezone, boundaryHour);
}

/**
 * 営業日1日分のFirestoreクエリ用UTC範囲を取得
 * 例: dateStr='2026-02-24', tz='Asia/Tokyo', boundary=6
 * → start = 2026-02-24 06:00 JST → UTC, end = 2026-02-25 06:00 JST → UTC
 *
 * boundary=0 の場合:
 * → start = 2026-02-24 00:00 JST → UTC, end = 2026-02-25 00:00 JST → UTC
 */
export function getBusinessDayRange(
  dateStr: string,
  timezone: string = 'Asia/Tokyo',
  boundaryHour: number = 0
): { start: Date; end: Date } {
  const [year, month, day] = dateStr.split('-').map(Number);
  const start = new TZDate(year, month - 1, day, boundaryHour, 0, 0, 0, timezone);
  const end = new TZDate(year, month - 1, day + 1, boundaryHour, 0, 0, 0, timezone);
  return { start: new Date(start.getTime()), end: new Date(end.getTime()) };
}

/**
 * 営業日ベースの日付範囲を取得（開始日〜終了日）
 * Analytics等で複数日の範囲クエリに使用
 */
export function getBusinessDateRange(
  startDateStr: string,
  endDateStr: string,
  timezone: string = 'Asia/Tokyo',
  boundaryHour: number = 0
): { start: Date; end: Date } {
  const [sYear, sMonth, sDay] = startDateStr.split('-').map(Number);
  const [eYear, eMonth, eDay] = endDateStr.split('-').map(Number);
  const start = new TZDate(sYear, sMonth - 1, sDay, boundaryHour, 0, 0, 0, timezone);
  const end = new TZDate(eYear, eMonth - 1, eDay + 1, boundaryHour, 0, 0, 0, timezone);
  return { start: new Date(start.getTime()), end: new Date(end.getTime()) };
}

/**
 * 店舗タイムゾーンでの今月の営業日範囲を取得
 */
export function getBusinessMonthRange(
  timezone: string = 'Asia/Tokyo',
  boundaryHour: number = 0,
  referenceDate?: Date
): { start: Date; end: Date; startStr: string; endStr: string } {
  const now = referenceDate
    ? new TZDate(referenceDate, timezone)
    : nowInShopTimezone(timezone);

  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);

  const startStr = format(monthStart, 'yyyy-MM-dd');
  const endStr = format(monthEnd, 'yyyy-MM-dd');

  return {
    ...getBusinessDateRange(startStr, endStr, timezone, boundaryHour),
    startStr,
    endStr,
  };
}

/**
 * 営業日の区切り時刻を決める唯一の入口。
 *
 * これを作った理由:
 *   同じ `shop.businessDayBoundaryTime` に、既定値が2種類あった。
 *     Dashboard.tsx        … ?? 6
 *     Checkout / Analytics / Payroll / Payouts / BackApproval … ?? 0
 *   区切りが未設定の店では、同じ一夜の売上が
 *   ダッシュボードと会計・分析で別の日に載る。
 *
 *   アプリ側はさらに悪く、Shop モデルが非null int の既定0で読むため、
 *   `?? 6` と書いても shop 自体が null のときしか効かず、実質すべて 0 だった。
 *
 *   LP とマニュアルは「営業日は朝6時で区切る」と約束している。
 *   申し込み時（shop-provisioning.ts）も 6 を入れる。
 *   未設定の店だけが 0 に落ちるのは、約束と食い違う。
 *
 * 対になる実装: shop_staff_app_v2/lib/utils/business_date_utils.dart の
 *              BusinessDateUtils.resolveBoundary
 */
export const DEFAULT_BOUNDARY_HOUR = 6;

export function resolveBoundaryHour(
  shop: { businessDayBoundaryTime?: number | null } | null | undefined
): number {
  const raw = shop?.businessDayBoundaryTime;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return DEFAULT_BOUNDARY_HOUR;
  if (!Number.isInteger(raw) || raw < 0 || raw > 23) return DEFAULT_BOUNDARY_HOUR;
  return raw;   // 0 を明示的に設定した店は 0 のまま（`||` では潰れる）
}
